import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { documentsApi, getApiErrorCode } from '@services/api';
import { useAuth } from '@hooks';
import {
  formatMoney,
  formatDate,
  formatDateTime,
  isQuoteExpired,
  legalActions,
} from '@utils';
import type { LifecycleAction } from '@utils';
import { LoadingSpinner, EmptyState, ConfirmModal } from '@components/common';
import {
  TypeBadge,
  StatusBadge,
  ExpiredBadge,
  ImportedBadge,
} from '@components/documents/badges';
import { VoidDialog } from '@components/documents/VoidDialog';
import type { Artifact, StatusEvent } from '@types';

const card = 'rounded-2xl border border-hairline bg-white p-5 shadow-sm';
const cardHead = 'text-sm font-semibold text-gray-900';
const primaryBtn =
  'inline-flex h-11 items-center gap-2 rounded-[10px] px-5 text-sm font-semibold text-white shadow-sm';
const ghostBtn =
  'inline-flex h-11 items-center gap-2 rounded-[10px] border-[1.5px] border-[#d7dfe9] bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50';
const dangerBtn =
  'inline-flex h-11 items-center gap-2 rounded-[10px] border-[1.5px] border-red-200 bg-white px-4 text-sm font-semibold text-red-700 hover:bg-red-50';

type DialogKind = 'issue' | 'mark-paid' | 'delete' | 'convert' | null;

export default function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [voidOpen, setVoidOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['document', id],
    queryFn: () => documentsApi.get(id!),
  });

  const artifactsQuery = useQuery({
    queryKey: ['artifacts', id],
    queryFn: () => documentsApi.artifacts(id!),
    enabled: Boolean(detailQuery.data && detailQuery.data.status !== 'DRAFT'),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['document', id] });
    queryClient.invalidateQueries({ queryKey: ['artifacts', id] });
    queryClient.invalidateQueries({ queryKey: ['documents'] });
  };

  const issueMutation = useMutation({
    mutationFn: () => documentsApi.issue(id!),
    onSuccess: updated => {
      invalidate();
      setDialog(null);
      toast.success(`Issued as ${updated.doc_no}`);
    },
    onError: () => toast.error('Could not issue the document'),
  });

  const onActionSuccess = (message: string) => () => {
    invalidate();
    setDialog(null);
    toast.success(message);
  };
  const onActionError = () => toast.error('Action failed');

  const acceptMutation = useMutation({
    mutationFn: () => documentsApi.accept(id!),
    onSuccess: onActionSuccess('Quote accepted'),
    onError: onActionError,
  });
  const declineMutation = useMutation({
    mutationFn: () => documentsApi.decline(id!),
    onSuccess: onActionSuccess('Quote declined'),
    onError: onActionError,
  });
  const markPaidMutation = useMutation({
    mutationFn: () => documentsApi.markPaid(id!),
    onSuccess: onActionSuccess('Marked paid'),
    onError: onActionError,
  });

  const voidMutation = useMutation({
    mutationFn: (reason: string) => documentsApi.void(id!, reason),
    onSuccess: () => {
      invalidate();
      setVoidOpen(false);
      toast.success('Document voided');
    },
    onError: e => {
      if (getApiErrorCode(e) === 'FORBIDDEN') {
        toast.error('Only an owner may void a paid invoice.');
      } else {
        toast.error('Could not void the document');
      }
    },
  });

  const convertMutation = useMutation({
    mutationFn: () => documentsApi.convert(id!),
    onSuccess: invoice => {
      invalidate();
      setDialog(null);
      toast.success(`Converted → ${invoice.doc_no}`);
      navigate(`/documents/${invoice.id}`);
    },
    onError: () => toast.error('Could not convert the quote'),
  });

  const duplicateMutation = useMutation({
    mutationFn: () => documentsApi.duplicate(id!),
    onSuccess: draft => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Draft created');
      navigate(`/documents/${draft.id}/edit`);
    },
    onError: () => toast.error('Could not duplicate'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => documentsApi.remove(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setDialog(null);
      toast.success('Draft deleted');
      navigate('/documents');
    },
    onError: () => toast.error('Could not delete the draft'),
  });

  const retryPdfMutation = useMutation({
    mutationFn: (versionNo: number) => documentsApi.retryPdf(id!, versionNo),
    onSuccess: () => {
      invalidate();
      toast.success('PDF generated');
    },
    onError: e => {
      const code = getApiErrorCode(e);
      if (code === 'PDF_ALREADY_SET') {
        toast.error('A PDF already exists for this version.');
        invalidate();
      } else if (code === 'CONVERSION_FAILED') {
        toast.error('PDF conversion failed. Try again shortly.');
      } else {
        toast.error('Could not generate the PDF.');
      }
    },
  });

  if (detailQuery.isLoading) {
    return (
      <div data-testid="detail-loading" className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div data-testid="detail-error">
        <EmptyState
          title="Couldn't load this document"
          description="Something went wrong. Please try again."
          action={{ label: 'Retry', onClick: () => detailQuery.refetch() }}
        />
      </div>
    );
  }

  const doc = detailQuery.data;
  const role = user?.role ?? 'staff';
  const actions = legalActions({
    status: doc.status,
    type: doc.type,
    role,
    imported: doc.imported,
  });
  const can = (a: LifecycleAction) => actions.includes(a);

  const artifacts = artifactsQuery.data?.artifacts ?? [];
  const byVersion = new Map<number, Artifact>(
    artifacts.map(a => [a.version_no, a])
  );
  const currentArtifact = byVersion.get(doc.current_version_no);
  const expired = isQuoteExpired(doc);

  return (
    <div data-testid="document-detail" className="space-y-6">
      {/* Breadcrumb */}
      <div className="font-mono text-[12px] text-gray-400">
        <Link to="/documents" className="hover:text-gray-600">
          Documents
        </Link>{' '}
        / <span className="text-gray-600">{doc.doc_no ?? 'Draft'}</span>
      </div>

      {/* Header + action hierarchy */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h1
              data-testid="detail-doc-no"
              className="font-mono text-[26px] font-semibold tracking-tight text-gray-900"
            >
              {doc.doc_no ?? 'DRAFT'}
            </h1>
            <TypeBadge type={doc.type} />
            <StatusBadge status={doc.status} />
            {expired && <ExpiredBadge />}
            {doc.imported && <ImportedBadge />}
          </div>
          <p className="mt-2 text-[14.5px] text-gray-600">
            {doc.client_name}
            {doc.event_name ? (
              <>
                {' '}
                <span className="text-gray-300">·</span> {doc.event_name}
              </>
            ) : (
              ''
            )}
          </p>
        </div>

        {/* Lifecycle actions — legal only (legalActions), primary emphasised */}
        <div
          className="flex flex-wrap items-center gap-2.5"
          data-testid="detail-actions"
        >
          {doc.imported && (
            <span
              data-testid="imported-notice"
              className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600"
            >
              View / download only
            </span>
          )}
          {can('issue') && (
            <button
              type="button"
              data-testid="action-issue"
              onClick={() => setDialog('issue')}
              className={`${primaryBtn} bg-primary-600 hover:bg-primary-700`}
            >
              Issue
            </button>
          )}
          {can('accept') && (
            <button
              type="button"
              data-testid="action-accept"
              onClick={() => acceptMutation.mutate()}
              className={`${primaryBtn} bg-green-600 hover:bg-green-700`}
            >
              Accept quote
            </button>
          )}
          {can('mark-paid') && (
            <button
              type="button"
              data-testid="action-mark-paid"
              onClick={() => setDialog('mark-paid')}
              className={`${primaryBtn} bg-emerald-600 hover:bg-emerald-700`}
            >
              Mark paid
            </button>
          )}
          {can('convert') && (
            <button
              type="button"
              data-testid="action-convert"
              onClick={() => setDialog('convert')}
              className={`${primaryBtn} bg-purple-600 hover:bg-purple-700`}
            >
              Convert to invoice
            </button>
          )}
          {can('edit') && (
            <Link
              to={`/documents/${doc.id}/edit`}
              data-testid="action-edit"
              className={ghostBtn}
            >
              {doc.status === 'DRAFT' ? 'Edit' : 'Edit & regenerate'}
            </Link>
          )}
          {can('duplicate') && (
            <button
              type="button"
              data-testid="action-duplicate"
              onClick={() => duplicateMutation.mutate()}
              className={ghostBtn}
            >
              Duplicate
            </button>
          )}
          {can('decline') && (
            <button
              type="button"
              data-testid="action-decline"
              onClick={() => declineMutation.mutate()}
              className={dangerBtn}
            >
              Decline
            </button>
          )}
          {can('void') && (
            <button
              type="button"
              data-testid="action-void"
              onClick={() => setVoidOpen(true)}
              className={dangerBtn}
            >
              Void…
            </button>
          )}
          {can('delete') && (
            <button
              type="button"
              data-testid="action-delete"
              onClick={() => setDialog('delete')}
              className={dangerBtn}
            >
              Delete draft
            </button>
          )}
        </div>
      </div>

      {/* Imported guidance banner */}
      {doc.imported && (
        <div className="flex items-start gap-3 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3.5">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#64748b"
            strokeWidth="2"
            className="mt-px flex-shrink-0"
          >
            <circle cx="12" cy="12" r="9" />
            <path
              d="M12 8h.01M11 12h1v4h1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div>
            <p className="text-[13px] font-semibold text-gray-700">
              View / download only
            </p>
            <p className="mt-0.5 text-[12.5px] text-gray-500">
              This record was imported from the legacy archive. It can be
              downloaded or duplicated into a new draft, but not edited, issued,
              or voided.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
        {/* LEFT */}
        <div className="space-y-6">
          {/* PDF preview / pending */}
          {doc.status !== 'DRAFT' && (
            <div className={card}>
              <div className="mb-3.5 flex items-center justify-between">
                <span className={cardHead}>Preview</span>
                <span className="font-mono text-[11px] text-gray-400">
                  v{doc.current_version_no} · signed link expires in 5 min
                </span>
              </div>
              {currentArtifact?.pdf_preview_url ? (
                <iframe
                  data-testid="pdf-preview"
                  title="PDF preview"
                  src={currentArtifact.pdf_preview_url}
                  className="h-[540px] w-full rounded-lg border border-hairline"
                />
              ) : (
                <div
                  data-testid="pdf-pending"
                  className="rounded-xl border-[1.5px] border-dashed border-gray-300 bg-gray-50 p-9 text-center"
                >
                  <div className="mx-auto mb-3.5 flex h-12 w-12 items-center justify-center rounded-xl border border-hairline bg-white">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#94a3b8"
                      strokeWidth="1.7"
                    >
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <path d="M14 2v6h6" />
                    </svg>
                  </div>
                  <p className="text-[13.5px] font-semibold text-gray-700">
                    PDF pending
                  </p>
                  <p className="mx-auto mt-1 max-w-xs text-[12.5px] text-gray-500">
                    The DOCX is ready; the PDF hasn't been generated yet.
                  </p>
                  <button
                    type="button"
                    data-testid="retry-pdf"
                    disabled={retryPdfMutation.isPending}
                    onClick={() =>
                      retryPdfMutation.mutate(doc.current_version_no)
                    }
                    className="mt-4 inline-flex h-10 items-center rounded-[9px] bg-primary-600 px-4 text-[13.5px] font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {retryPdfMutation.isPending ? 'Generating…' : 'Retry PDF'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Line items */}
          <div className={card}>
            <span className={cardHead}>Line items</span>
            <table
              className="mt-3.5 min-w-full text-sm"
              data-testid="detail-line-items"
            >
              <tbody>
                {doc.line_items.map(li => (
                  <tr key={li.position} className="border-t border-gray-100">
                    <td className="py-2.5 pr-4 text-gray-700">
                      {li.description}
                    </td>
                    <td className="py-2.5 text-right font-mono font-medium text-gray-900">
                      {formatMoney(li.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-[1.5px] border-hairline">
                <tr>
                  <td className="pt-3 text-right text-[13px] text-gray-500">
                    Subtotal
                  </td>
                  <td className="pt-3 text-right font-mono text-gray-700">
                    {formatMoney(doc.subtotal)}
                  </td>
                </tr>
                <tr>
                  <td className="text-right text-[13px] text-gray-500">
                    Discount
                  </td>
                  <td className="text-right font-mono text-gray-700">
                    −{formatMoney(doc.discount)}
                  </td>
                </tr>
                <tr>
                  <td className="pt-1.5 text-right font-bold text-gray-900">
                    Total
                  </td>
                  <td
                    data-testid="detail-total"
                    className="pt-1.5 text-right font-mono text-base font-bold text-gray-900"
                  >
                    {formatMoney(doc.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Version history */}
          {doc.versions.length > 0 && (
            <div className={card}>
              <span className={cardHead}>Version history</span>
              <ul className="mt-3.5 space-y-2.5" data-testid="version-history">
                {[...doc.versions]
                  .sort((a, b) => b.version_no - a.version_no)
                  .map(v => {
                    const art = byVersion.get(v.version_no);
                    return (
                      <li
                        key={v.version_no}
                        data-testid="version-row"
                        className="flex flex-wrap items-center justify-between gap-2 rounded-[10px] border border-[#eef2f7] bg-[#fbfcfe] px-3.5 py-3"
                      >
                        <div>
                          <span className="font-mono text-[13.5px] font-medium text-gray-900">
                            {doc.doc_no}
                            {v.revision_label ? `-${v.revision_label}` : ''}
                          </span>
                          <span className="ml-2.5 font-mono text-[11px] text-gray-400">
                            v{v.version_no}
                            {v.created_at
                              ? ` · ${formatDateTime(v.created_at)}`
                              : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {art?.docx_url && (
                            <a
                              data-testid={`download-docx-${v.version_no}`}
                              href={art.docx_url}
                              className="rounded-[7px] border border-primary-200 px-2.5 py-1 text-[12.5px] font-semibold text-primary-600 hover:bg-primary-50"
                            >
                              DOCX
                            </a>
                          )}
                          {art?.pdf_url ? (
                            <a
                              data-testid={`download-pdf-${v.version_no}`}
                              href={art.pdf_url}
                              className="rounded-[7px] border border-primary-200 px-2.5 py-1 text-[12.5px] font-semibold text-primary-600 hover:bg-primary-50"
                            >
                              PDF
                            </a>
                          ) : (
                            <span className="text-[11px] text-gray-400">
                              PDF pending
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div className="space-y-6">
          <div className={card}>
            <span className={cardHead}>Details</span>
            <dl className="mt-3.5">
              <Row label="Client" value={doc.client_name ?? '—'} />
              <Row label="Ref" value={doc.client_short_ref ?? '—'} mono />
              <Row label="Contact" value={doc.contact ?? '—'} />
              <Row
                label="Issue date"
                value={doc.issue_date ? formatDate(doc.issue_date) : '—'}
              />
              {doc.type === 'invoice' ? (
                <Row
                  label="Due date"
                  value={doc.due_date ? formatDate(doc.due_date) : '—'}
                />
              ) : (
                <Row
                  label="Valid until"
                  value={
                    doc.valid_until ? formatDate(doc.valid_until) : 'Not set'
                  }
                  muted={!doc.valid_until}
                />
              )}
              <Row label="Prepared by" value={doc.prepared_label ?? '—'} last />
            </dl>
          </div>

          {/* Links (F5.4/F5.5) */}
          {(doc.links.source_quote_id ||
            doc.links.converted_invoice_id ||
            doc.links.replaces_document_id) && (
            <div className={card} data-testid="document-links">
              <span className={cardHead}>Linked documents</span>
              <ul className="mt-3 space-y-2 text-sm">
                {doc.links.source_quote_id && (
                  <li>
                    <Link
                      data-testid="link-source-quote"
                      to={`/documents/${doc.links.source_quote_id}`}
                      className="flex items-center gap-2.5 rounded-[10px] border border-[#eef2f7] px-3.5 py-2.5 hover:bg-gray-50"
                    >
                      <LinkGlyph tone="quote">←</LinkGlyph>
                      <span className="font-medium text-gray-900">
                        Source quote
                      </span>
                    </Link>
                  </li>
                )}
                {doc.links.converted_invoice_id && (
                  <li>
                    <Link
                      data-testid="link-converted-invoice"
                      to={`/documents/${doc.links.converted_invoice_id}`}
                      className="flex items-center gap-2.5 rounded-[10px] border border-[#eef2f7] px-3.5 py-2.5 hover:bg-gray-50"
                    >
                      <LinkGlyph tone="invoice">→</LinkGlyph>
                      <span className="font-medium text-gray-900">
                        Converted invoice
                      </span>
                    </Link>
                  </li>
                )}
                {doc.links.replaces_document_id && (
                  <li>
                    <Link
                      data-testid="link-replaces"
                      to={`/documents/${doc.links.replaces_document_id}`}
                      className="flex items-center gap-2.5 rounded-[10px] border border-[#eef2f7] px-3.5 py-2.5 hover:bg-gray-50"
                    >
                      <LinkGlyph tone="replace">⤺</LinkGlyph>
                      <span className="font-medium text-gray-900">
                        Replaces prior invoice
                      </span>
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Lifecycle timeline (status history, F5.7) */}
          {doc.status_history.length > 0 && (
            <div className={card}>
              <span className={cardHead}>Lifecycle</span>
              <ol className="relative mt-4 pl-1.5" data-testid="status-history">
                <span className="absolute left-[11px] top-1.5 bottom-3.5 w-0.5 bg-hairline" />
                {[...doc.status_history].reverse().map((ev, i) => (
                  <TimelineItem key={i} ev={ev} latest={i === 0} />
                ))}
              </ol>
            </div>
          )}

          {/* Activity with before/after (F7.3) */}
          <div className={card}>
            <span className={cardHead}>Activity</span>
            <ol className="mt-3.5 space-y-3.5" data-testid="activity-feed">
              {doc.activity.map((ev, i) => {
                const diffs = fieldDiffs(ev.old_values, ev.new_values);
                return (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="mt-1.5 h-[7px] w-[7px] flex-shrink-0 rounded-full bg-primary-500" />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-800">{ev.action}</p>
                      {diffs.length > 0 && (
                        <div className="mt-1.5 space-y-1">
                          {diffs.map(d => (
                            <div
                              key={d.field}
                              className="flex flex-wrap items-center gap-2"
                            >
                              <span className="font-mono text-[10.5px] uppercase text-gray-400">
                                {d.field}
                              </span>
                              {d.from !== undefined && (
                                <>
                                  <span className="rounded-md bg-red-50 px-1.5 py-0.5 text-[12px] text-red-500 line-through">
                                    {d.from}
                                  </span>
                                  <span className="text-gray-300">→</span>
                                </>
                              )}
                              <span className="rounded-md bg-green-50 px-1.5 py-0.5 text-[12px] text-green-700">
                                {d.to}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="mt-1 font-mono text-[11px] text-gray-400">
                        {ev.actor ?? 'system'}
                        {ev.at ? ` · ${formatDateTime(ev.at)}` : ''}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>

      {/* Confirm dialogs */}
      <ConfirmModal
        isOpen={dialog === 'issue'}
        onClose={() => setDialog(null)}
        onConfirm={() => issueMutation.mutate()}
        isLoading={issueMutation.isPending}
        variant="info"
        title="Issue document"
        message="Issuing assigns the permanent document number and renders the first version. This cannot be undone."
        confirmLabel="Confirm issue"
      />
      <ConfirmModal
        isOpen={dialog === 'mark-paid'}
        onClose={() => setDialog(null)}
        onConfirm={() => markPaidMutation.mutate()}
        isLoading={markPaidMutation.isPending}
        variant="info"
        title="Mark invoice paid"
        message="Mark this invoice as PAID? A paid invoice is immutable — corrections require a void + replacement."
        confirmLabel="Mark paid"
      />
      <ConfirmModal
        isOpen={dialog === 'convert'}
        onClose={() => setDialog(null)}
        onConfirm={() => convertMutation.mutate()}
        isLoading={convertMutation.isPending}
        variant="info"
        title="Convert quote to invoice"
        message="This creates a new linked invoice with its own number and leaves the quote untouched (marked CONVERTED)."
        confirmLabel="Confirm convert"
      />
      <ConfirmModal
        isOpen={dialog === 'delete'}
        onClose={() => setDialog(null)}
        onConfirm={() => deleteMutation.mutate()}
        isLoading={deleteMutation.isPending}
        variant="danger"
        title="Delete draft"
        message="Delete this draft permanently? Only drafts can be deleted."
        confirmLabel="Delete"
      />
      <VoidDialog
        isOpen={voidOpen}
        onClose={() => setVoidOpen(false)}
        onConfirm={reason => voidMutation.mutate(reason)}
        isLoading={voidMutation.isPending}
        fromPaid={doc.status === 'PAID'}
      />
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  muted,
  last,
}: {
  label: string;
  value: string;
  mono?: boolean;
  muted?: boolean;
  last?: boolean;
}) {
  return (
    <div
      className={`flex justify-between gap-4 py-2 ${last ? '' : 'border-b border-[#f4f6fa]'}`}
    >
      <dt className="text-[13px] text-gray-500">{label}</dt>
      <dd
        className={`text-right text-[13px] ${muted ? 'text-gray-400' : 'text-gray-900'} ${mono ? 'font-mono text-[12.5px]' : ''}`}
      >
        {value}
      </dd>
    </div>
  );
}

function LinkGlyph({
  tone,
  children,
}: {
  tone: 'quote' | 'invoice' | 'replace';
  children: React.ReactNode;
}) {
  const styles = {
    quote: 'bg-teal-50 text-teal-700',
    invoice: 'bg-purple-50 text-purple-700',
    replace: 'bg-amber-50 text-amber-700',
  }[tone];
  return (
    <span
      className={`flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg text-[15px] ${styles}`}
    >
      {children}
    </span>
  );
}

// Status colours for the lifecycle timeline pill. Kept local (no
// `doc-status-badge` testid) so it never collides with the single header badge.
const TIMELINE_STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ISSUED: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  DECLINED: 'bg-red-100 text-red-800',
  CONVERTED: 'bg-purple-100 text-purple-800',
  VOID: 'bg-gray-200 text-gray-600 line-through',
  PAID: 'bg-emerald-100 text-emerald-800',
};

function TimelineItem({ ev, latest }: { ev: StatusEvent; latest: boolean }) {
  return (
    <li className="relative pb-5 pl-[30px] last:pb-0">
      <span
        className={`absolute left-1 top-0.5 h-4 w-4 rounded-full ${
          latest
            ? 'border-[3px] border-primary-600 bg-primary-100'
            : 'border-[2.5px] border-gray-300 bg-white'
        }`}
      />
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${TIMELINE_STATUS_STYLES[ev.to] ?? 'bg-gray-100 text-gray-700'}`}
        >
          {ev.to}
        </span>
        {ev.from && (
          <span className="text-[12px] text-gray-400">from {ev.from}</span>
        )}
      </div>
      {ev.note && (
        <div
          data-testid="status-note"
          className="mt-1.5 rounded-md bg-red-50 px-2 py-1 text-[12px] text-red-600"
        >
          Reason: {ev.note}
        </div>
      )}
      <div className="mt-1 font-mono text-[11px] text-gray-400">
        {ev.actor ?? 'system'}
        {ev.at ? ` · ${formatDateTime(ev.at)}` : ''}
      </div>
    </li>
  );
}

/** Render human-readable before/after pairs from an activity event's value maps. */
function fieldDiffs(
  oldValues: Record<string, unknown> | null,
  newValues: Record<string, unknown> | null
): Array<{ field: string; from?: string; to: string }> {
  if (!newValues) return [];
  const out: Array<{ field: string; from?: string; to: string }> = [];
  for (const [field, next] of Object.entries(newValues)) {
    const prev = oldValues?.[field];
    const to = String(next);
    if (prev === undefined || prev === null) {
      out.push({ field, to });
    } else if (String(prev) !== to) {
      out.push({ field, from: String(prev), to });
    }
  }
  return out.slice(0, 4);
}
